import makeUniqueProductId from '@scentregroup/shared/helpers/make-unique-product-id'
import { categoryToString } from '@scentregroup/shared/helpers/product-category'
import { IUser } from '@scentregroup/shared/types/user'
import { IMarketplaceCartItem } from '../types/cart'
import { IMarketplaceCheckout } from '../types/marketplace-checkout'
import { MarketplaceCoupon } from '@scentregroup/shared/types'
import {
  associatePromotionsWithCoupons,
  classifyAppliedPromotions,
} from '@scentregroup/shared/helpers/marketplace-promotions'
import { isMarketplaceCouponEligible } from './marketplace-coupon'

export enum CheckoutStepOrder {
  FULFILMENT = '2',
  PAYMENT = '3',
}

export enum CheckoutOption {
  APPLE_PAY = 'Apple Pay',
  CREDIT_CARD = 'Credit Card',
  GOOGLE_PAY = 'Google Pay',
}

export interface ICheckoutTagging {
  'data-ecomm-product': string
  'data-product-brand': string
  'data-product-category': string | null
  'data-product-retailer': string
  'data-product-retailer-id': string
  'data-product-id': string
  'data-product-name': string
  'data-product-price': number
  'data-product-quantity': number
  'data-product-currency'?: string
  'data-product-variant': string
  'data-product-option': string
  'data-product-step': string
  'data-scentregroupid'?: string
  'data-product-auto-promotion-id'?: string
  'data-product-auto-promotion-name'?: string
  'data-product-coupon-promotion-id'?: string
  'data-product-coupon-promotion-name'?: string
}

export const makeCheckoutTagging = (
  checkoutItems: IMarketplaceCartItem[],
  scentregroupId: string | undefined,
  currency: string | undefined,
  step: string,
  coupons?: MarketplaceCoupon[]
): ICheckoutTagging[] => {
  return checkoutItems.map(
    ({
      id,
      name,
      listPrice,
      quantity,
      shop,
      brand,
      category,
      optionValues,
      sku,
      promotions,
      variantGroupCode,
    }) => {
      const [appliedCouponPromotions, appliedAutoPromotions] =
        classifyAppliedPromotions(
          associatePromotionsWithCoupons({
            promotions,
            coupons: coupons?.filter(isMarketplaceCouponEligible),
          })
        )
      const appliedCouponPromotion = appliedCouponPromotions.length
        ? appliedCouponPromotions[0]
        : undefined
      const appliedAutoPromotion = appliedAutoPromotions.length
        ? appliedAutoPromotions[0]
        : undefined

      return {
        'data-ecomm-product': 'product',
        'data-product-brand': brand,
        'data-product-category': categoryToString(category),
        'data-product-retailer': shop.name,
        'data-product-retailer-id': shop.retailer?.sys.id ?? '',
        'data-product-id': variantGroupCode ?? '',
        'data-product-unique-id': makeUniqueProductId(id, shop.id, sku),
        'data-product-name': name,
        'data-product-price': listPrice,
        'data-product-quantity': quantity,
        'data-product-currency': currency,
        'data-product-variant': optionValues.map(o => o.value).join('|'),
        'data-product-variant-sku': sku,
        'data-product-option': scentregroupId ? 'loggedin' : 'guest',
        'data-product-step': step,
        'data-scentregroupid': scentregroupId,
        ...(appliedCouponPromotion && {
          'data-product-coupon-promotion-id': appliedCouponPromotion.campaignId,
          'data-product-coupon-promotion-name': appliedCouponPromotion.title,
          'data-product-promotion-code': appliedCouponPromotion.couponCode,
        }),
        ...(appliedAutoPromotion && {
          'data-product-auto-promotion-id': appliedAutoPromotion.campaignId,
          'data-product-auto-promotion-name': appliedAutoPromotion.title,
          'data-product-promotion-code': `NoCoupon_${appliedAutoPromotion.campaignId}`,
        }),
      }
    }
  )
}

export function generateProductsAnalytics(
  checkoutDetails: IMarketplaceCheckout,
  user: IUser | undefined,
  step: CheckoutStepOrder,
  coupons?: MarketplaceCoupon[]
): ICheckoutTagging[] {
  const checkoutItems = checkoutDetails.parcels
    .map(parcel => parcel.items)
    .reduce((acc, items) => {
      return acc.concat(items)
    }, [])

  return makeCheckoutTagging(
    checkoutItems,
    user?.scentregroupId,
    checkoutDetails?.currency,
    step,
    coupons
  )
}
